import { useMemo, useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  createWishRequestSchema,
  uuidv7,
  wishContentSchema,
} from "@vmeste/shared";
import { useSession } from "../app/session";
import { Button, Card, TextField } from "../components/ui";
import { ApiError, api } from "../lib/api";
import { syncEngine } from "../lib/sync";
import { validate } from "../lib/form";
import { useT, type Translate } from "../i18n/index.js";

/**
 * Приём ссылки со стороны — «Поделиться» (SPEC §4.3).
 *
 * Две дороги ведут на этот экран и обе кончаются одним и тем же:
 *
 * - Android: установленная PWA зарегистрирована как Web Share Target, система
 *   открывает `/share?title=…&text=…&url=…` (см. `share_target` в vite.config).
 * - iOS: такого API нет вовсе, поэтому Shortcut в share sheet открывает тот же
 *   адрес руками (docs/SHARING.md).
 *
 * Share target объявлен как GET намеренно: POST потребовал бы, чтобы service
 * worker перехватывал запрос и пересобирал его, — больше механики и больше
 * способов потерять чужую ссылку.
 *
 * Экран ничего не решает за человека: показывает, что именно пришло, даёт
 * поправить название и сохраняет как хотелку. Ссылка нигде не задерживается —
 * из адресной строки она уходит прямо в content-блоб (SPEC §3), в логи и в
 * аналитику не попадает, потому что ни того, ни другого здесь нет.
 */

/*
 * Кодек берётся у пространства, в которое сохраняем (SPEC §3). Если его нет —
 * пространство зашифровано, а ключа на этом устройстве не оказалось, — экран
 * говорит об этом прямо. Записать открытым текстом было бы хуже всего: чужая
 * ссылка легла бы читаемой туда, где всё остальное нечитаемо, и молча.
 */

/** Ограничения из wishContentSchema — тот же источник правды, что у сервера. */
const URL_LIMIT = 2_000;
const TITLE_LIMIT = 300;
const NOTE_LIMIT = 10_000;

// Built at call time, not once at module scope: a table of strings frozen on
// import would keep the language the app started in.
function fieldMessages(t: Translate): Record<string, string> {
  return {
    title: t("share.errorTitle"),
    url: t("share.errorUrl"),
    note: t("share.errorNote"),
  };
}

/* ----------------------------------------------------------- разбор ссылки */

/**
 * Кавычки и скобки в конец адреса чаще попадают из окружающего текста, чем
 * принадлежат ему. Закрывающая скобка — исключение: у неё бывает пара внутри
 * самой ссылки (статьи в Википедии), и тогда её надо оставить.
 */
const TAIL = ".,;:!?…»\"'";

function occurrences(value: string, char: string): number {
  let count = 0;
  for (const ch of value) if (ch === char) count += 1;
  return count;
}

function trimTail(raw: string): string {
  let value = raw;

  for (;;) {
    const last = value.at(-1);
    if (!last) return value;

    if (TAIL.includes(last)) {
      value = value.slice(0, -1);
      continue;
    }

    if (last === ")" || last === "]") {
      const body = value.slice(0, -1);
      const opener = last === ")" ? "(" : "[";
      if (occurrences(body, opener) <= occurrences(body, last)) {
        value = body;
        continue;
      }
    }

    return value;
  }
}

/** Первый http(s)-адрес в строке. Пробелы и типографские кавычки — граница. */
const URL_IN_TEXT = /https?:\/\/[^\s<>"'«»]+/i;

/** Голый домен — принимается только из поля `url`, где адрес обещан контрактом. */
const BARE_HOST = /^[\w-]+(\.[\w-]+)+(\/\S*)?$/;

function looksLikeUrl(value: string): boolean {
  try {
    const parsed = new URL(value);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
}

interface Found {
  /** Адрес в том виде, в каком им поделились: нормализация только ломает. */
  url: string;
  /** Ровно тот кусок, что был вырезан из текста, — чтобы вычесть его оттуда. */
  raw: string;
  field: "url" | "text" | "title";
}

function pick(value: string, field: Found["field"]): Found | null {
  const match = URL_IN_TEXT.exec(value);
  if (match) {
    const url = trimTail(match[0]);
    if (looksLikeUrl(url)) return { url, raw: match[0], field };
  }

  // «ozon.ru/product/123» без схемы. Только из `url`: в свободном тексте так же
  // выглядит обычное слово с точкой, и угадывать там — терять чужой текст.
  if (field === "url") {
    const bare = value.trim();
    if (BARE_HOST.test(bare) && looksLikeUrl(`https://${bare}`)) {
      return { url: `https://${bare}`, raw: bare, field };
    }
  }

  return null;
}

interface Shared {
  title: string;
  text: string;
  url: string;
}

interface Parsed {
  url: string | null;
  /** Длиннее лимита — такой адрес не влезает в `url`, но терять его нельзя. */
  oversizedUrl: string | null;
  title: string;
  note: string;
}

/**
 * Что из трёх полей чем является.
 *
 * Chrome кладёт адрес в `url`, но так делают не все: половина приложений шлёт
 * всё одной строкой в `text` («Смотри что нашёл https://ozon.ru/…»), часть —
 * заголовок отдельно, а адрес внутри текста. Поэтому порядок поиска — `url`,
 * `text`, `title`, и в каждом поле ищется адрес *внутри* строки, а не строка
 * целиком.
 */
function parseShare(shared: Shared): Parsed {
  const found =
    pick(shared.url, "url") ?? pick(shared.text, "text") ?? pick(shared.title, "title");

  const oversized = found && found.url.length > URL_LIMIT ? found.url : null;

  // Текст без вырезанного адреса — обычно это и есть человеческая подпись.
  const remainder = tidyEdges(
    (found?.field === "text" ? shared.text.replace(found.raw, " ") : shared.text)
      .replace(/\s+/g, " ")
      .trim(),
  );

  const explicit = shared.title.trim();

  let title = explicit || remainder;
  let note = explicit && remainder && remainder !== explicit ? remainder : "";

  // Совсем без слов — берём хост: «ozon.ru» лучше, чем пустое поле, и правится
  // одним касанием.
  if (!title && found) title = hostLabel(found.url);

  if (oversized) {
    // В `url` он не поместится, поэтому едет в заметку целиком: лучше некрасиво,
    // чем потерять.
    note = note ? `${note}\n${oversized}` : oversized;
  }

  return {
    url: oversized ? null : (found?.url ?? null),
    oversizedUrl: oversized,
    title: title.slice(0, TITLE_LIMIT),
    note: note.slice(0, NOTE_LIMIT),
  };
}

/**
 * Кайма из знаков препинания — след вырезанного адреса, а не название.
 * «(см. https://…)» без этого превратилось бы в хотелку «(см.».
 */
function tidyEdges(value: string): string {
  return value.replace(/^[\s\p{P}]+/u, "").replace(/[\s\p{P}]+$/u, "");
}

function hostLabel(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return "";
  }
}

/* ------------------------------------------------------------------ экран */

export function SharePage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { session, space, loading } = useSession();
  const t = useT();

  const shared = useMemo<Shared>(
    () => ({
      title: params.get("title") ?? "",
      text: params.get("text") ?? "",
      url: params.get("url") ?? "",
    }),
    [params],
  );

  const parsed = useMemo(() => parseShare(shared), [shared]);

  const [title, setTitle] = useState(parsed.title);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [failure, setFailure] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const spaceId = session?.spaceId ?? null;

  // Сессия живёт в куке, которую подтверждает сервер: пока первый ответ не
  // пришёл, мы не знаем ничего, и пустой экран здесь читался бы как поломка.
  if (loading) {
    return (
      <div className="grid min-h-[50dvh] place-items-center text-muted">
        <span className="sr-only">{t("common.loading")}</span>
        <span aria-hidden="true" className="size-6 animate-pulse rounded-full bg-line" />
      </div>
    );
  }

  // До этой ветки обычно не доходит — AppLayout уводит на /signin раньше, чем
  // экран смонтируется. Но молчать, если она всё же случится, нечестно.
  if (!session || !space || !spaceId) {
    return (
      <Notice
        title={t("share.signInTitle")}
        body={t("share.signInBody")}
        action={{ to: "/signin", label: t("share.signInAction") }}
      />
    );
  }

  const nothingCame = !parsed.url && !parsed.note && !parsed.title;

  if (nothingCame) {
    return (
      <Notice
        title={t("share.emptyTitle")}
        body={t("share.emptyBody")}
        action={{ to: "/wishes", label: t("share.emptyAction") }}
      />
    );
  }

  // Проверяем до формы, а не в момент сохранения: заполнять поля, чтобы потом
  // услышать «не вышло», — обман. Пространство без ключа не читается и здесь:
  // человек увидит замок раньше, чем начнёт печатать.
  const locked = syncEngine.codec(spaceId) === null;

  if (locked) {
    return (
      <Notice
        title={t("share.lockedTitle")}
        body={t("share.lockedBody")}
        action={{ to: "/wishes", label: t("share.emptyAction") }}
      />
    );
  }

  async function save(event: FormEvent) {
    event.preventDefault();
    if (!spaceId || saving) return;

    // Спрашиваем ещё раз, а не полагаемся на проверку при отрисовке: между ней
    // и нажатием «Сохранить» ключ могли забыть — выходом, например.
    const codec = syncEngine.codec(spaceId);
    if (!codec) return;

    const candidate = {
      title: title.trim(),
      ...(parsed.url ? { url: parsed.url } : {}),
      ...(parsed.note ? { note: parsed.note } : {}),
    };

    // Та же схема, которой всё это будет расшифровано при чтении: блоб, не
    // прошедший её, просто не отрисуется в списке.
    const content = validate(wishContentSchema, candidate, fieldMessages(t));
    if (!content.ok) {
      setErrors(content.errors);
      return;
    }

    setErrors({});
    setFailure(null);
    setSaving(true);

    try {
      const body = createWishRequestSchema.parse({
        // Свой id: повтор запроса после сорвавшейся сети не заведёт вторую
        // хотелку — сервер вернёт ту же (см. POST /wishes).
        id: uuidv7(),
        content: await codec.encode(content.value),
        status: "idea",
      });

      await api.createWish(spaceId, body);

      // Дельта придёт и сама по WebSocket, но человек уже смотрит на список —
      // пусть хотелка будет там к моменту перехода.
      await syncEngine.pull(spaceId).catch(() => {
        /* не доехало — подтянется следующим нудждом от сокета */
      });

      // replace, чтобы «назад» не вернуло на форму с той же ссылкой.
      navigate("/wishes", { replace: true });
    } catch (cause) {
      setFailure(cause instanceof ApiError ? cause.message : t("share.saveFailed"));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="flex flex-col gap-4 px-4 pt-4 pb-8">
      <header className="px-1">
        <h1 className="font-display text-2xl">{t("share.title")}</h1>
        <p className="mt-0.5 text-[0.875rem] text-muted">
          {t("share.inSpace", { name: space.name })}
        </p>
      </header>

      <Card>
        <div className="flex flex-col gap-2 px-4 py-3">
          <h2 className="text-[0.8125rem] font-semibold tracking-wide text-muted uppercase">
            {t("share.whatCame")}
          </h2>

          {parsed.url ? (
            <p className="font-mono text-[0.8125rem] break-all text-ink-soft">
              {parsed.url}
            </p>
          ) : (
            <p className="text-[0.875rem] text-muted">{t("share.noUrl")}</p>
          )}

          {parsed.oversizedUrl ? (
            <p className="text-[0.8125rem] text-warn">
              {t("share.oversizedUrl", { count: URL_LIMIT })}
            </p>
          ) : null}

          {parsed.note ? (
            <p className="text-[0.8125rem] whitespace-pre-line text-muted">
              {parsed.note}
            </p>
          ) : null}
        </div>
      </Card>

      <form className="flex flex-col gap-4" onSubmit={save} noValidate>
        <TextField
          label={t("share.titleLabel")}
          hint={t("share.titleHint")}
          value={title}
          error={errors["title"]}
          maxLength={TITLE_LIMIT}
          autoComplete="off"
          onChange={(event) => setTitle(event.target.value)}
        />

        {errors["url"] ? (
          <p role="alert" className="text-[0.8125rem] text-neg">
            {errors["url"]}
          </p>
        ) : null}

        {failure ? (
          <p
            role="alert"
            className="rounded-row bg-neg-soft px-4 py-3 text-[0.875rem] text-neg"
          >
            {failure}
          </p>
        ) : null}

        <div className="flex gap-2">
          <Button type="submit" disabled={saving || !title.trim()} className="flex-1">
            {saving ? t("share.saving") : t("common.save")}
          </Button>
          <Button type="button" variant="ghost" onClick={() => navigate("/wishes")}>
            {t("common.cancel")}
          </Button>
        </div>
      </form>
    </div>
  );
}

function Notice({
  title,
  body,
  action,
}: {
  title: string;
  body: string;
  action: { to: string; label: string };
}) {
  return (
    <div className="flex flex-col gap-4 px-4 pt-8 pb-8">
      <h1 className="font-display text-2xl">{title}</h1>
      <p className="text-ink-soft">{body}</p>
      <Link
        to={action.to}
        className="inline-flex min-h-[var(--touch)] items-center justify-center rounded-pill bg-accent px-5 text-[0.9375rem] font-semibold text-ground"
      >
        {action.label}
      </Link>
    </div>
  );
}
